import * as React from "react";
import { createBrowserRouter, Navigate, RouterProvider } from "react-router-dom";
import { createTheme, ThemeProvider } from "@mui/material";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "./index.css";
import "./App.css";
import { Homepage } from "./views/home";
import { Feed } from "./views/feed";
import { AccountList } from "./views/account/list";
import { AccountShow } from "./views/account/show";
import { AccountEdit } from "./views/account/edit";
import { MessageList } from "./views/message/list";
import { RegisterAccountProccess } from "./views/account/create";
import { MessageShow } from "./views/message/show";
import { MessageCreate } from "./views/message/create";
import { ErrorPage } from "./views/err.page";
import { PostShow } from "./views/post/show";
import { UserProvider } from "./context/UserContext";
import { NotificationList } from "./views/notification/list";
import { PostCreate } from "./views/post/create";
import { Meet } from "./views/meet";
import { CreateCommunity } from "./views/community/create";
import { DeleteCommunity } from "./views/community/delete";
import { EditCommunity } from "./views/community/edit";
import { ListCommunity } from "./views/community/list";
import { ShowCommunity } from "./views/community/show";
import { CreateEvent } from "./views/event/create";
import { ShowEvent } from "./views/event/show";
import { DeleteEvent } from "./views/event/delete";
import { EditEvent } from "./views/event/edit";
import { ListEvent } from "./views/event/list";
import { CreateForum } from "./views/forum/create";
import { ShowForum } from "./views/forum/show";
import { DeleteForum } from "./views/forum/delete";
import { EditForum } from "./views/forum/edit";
import { ListForum } from "./views/forum/list";
import { InterestsList } from "./views/interest/list";
import { ProfileCompleteProvider } from "./context/ProfileComplete";
import { ProfileEdit } from "./components/account/ProfileQuestions/ProfileEdit";
import SpeedMeeting from "./views/speed-meeting";
import { LocationTracker } from "./util/LocationTracker";
import { CheckinAlertListener } from "./util/CheckinAlertListener";
import { MingleList } from "./views/mingle";

const queryClient = new QueryClient();

const theme = createTheme({
  palette: {
    primary: {
      main: "#8c8dff",
      contrastText: "white",
    },
    secondary: {
      main: "#0B4F6C",
    },
  },
});

const withErrorBoundary = (routes) =>
  routes.map((route) => ({
    ...route,
    errorElement: route.errorElement || <ErrorPage />,
  }));

const cleanStoredAuthState = () => {
  // Remove corrupted/empty user objects but leave the token alone —
  // UserContext handles re-fetching the profile from the token.
  const rawUser = localStorage.getItem("user");
  if (!rawUser) return;
  try {
    const parsedUser = JSON.parse(rawUser);
    if (!parsedUser || typeof parsedUser !== "object" || Object.keys(parsedUser).length === 0) {
      localStorage.removeItem("user");
    }
  } catch {
    localStorage.removeItem("user");
  }
};

const router = createBrowserRouter(
  withErrorBoundary([
    { path: "/", element: <Homepage /> },
    { path: "/login", element: <Homepage /> },
    { path: "/register", element: <RegisterAccountProccess /> },
    { path: "/feed", element: <Feed /> },
    // Explore was retired — its public-browsing purpose now lives on The Tea's Public tab (with search).
    { path: "/explore", element: <Navigate to="/feed" replace /> },
    { path: "/members", element: <AccountList /> },
    { path: "/messages", element: <MessageList /> },
    { path: "/messages/create", element: <MessageCreate /> },
    { path: "/messages/:id/:conversation_id", element: <MessageShow /> },
    { path: "/profile/:user_id", element: <AccountShow /> },
    { path: "/settings", element: <AccountEdit /> },
    { path: "/profile/edit/:id", element: <ProfileEdit /> },
    { path: "/profile/manage", element: <RegisterAccountProccess /> },
    { path: "/post/:id", element: <PostShow /> },
    { path: "/post/create", element: <PostCreate /> },
    { path: "/notifications", element: <NotificationList /> },
    { path: "/meet", element: <Meet /> },
    { path: "/community/create", element: <CreateCommunity /> },
    { path: "/community/show/:id", element: <ShowCommunity /> },
    { path: "/community/delete/:id", element: <DeleteCommunity /> },
    { path: "/community/edit/:id", element: <EditCommunity /> },
    { path: "/community/list", element: <ListCommunity /> },
    { path: "/event/create/:community_id", element: <CreateEvent /> },
    { path: "/event/show/:id", element: <ShowEvent /> },
    { path: "/event/delete/:id", element: <DeleteEvent /> },
    { path: "/event/edit/:id", element: <EditEvent /> },
    { path: "/event/list", element: <ListEvent /> },
    { path: "/forum/create", element: <CreateForum /> },
    { path: "/forum/show/:id", element: <ShowForum /> },
    { path: "/forum/delete/:id", element: <DeleteForum /> },
    { path: "/forum/edit/:id", element: <EditForum /> },
    { path: "/forum/list", element: <ListForum /> },
    { path: "/interest/list", element: <InterestsList /> },
    { path: "/speed-meeting", element: <SpeedMeeting /> },
    { path: "/mingle", element: <MingleList /> },
    { path: "*", element: <ErrorPage /> },
  ])
);

function App() {
  // hide all console logs in production
  if (process.env.NODE_ENV === "production") {
    console.log = function () {};
  }
  
  cleanStoredAuthState();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <UserProvider>
          <ProfileCompleteProvider>
            <React.StrictMode>
              <LocationTracker />
              <CheckinAlertListener />
              <RouterProvider router={router} />
            </React.StrictMode>
          </ProfileCompleteProvider>
        </UserProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
